import { Upload } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { applicationSourceLabel, DEFAULT_IMPORT_SOURCE, type ImportListItem, type JobDetail } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { DropZone } from '../../../components/DropZone';
import { Field } from '../../../components/Field';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { ToneChip } from '../../../components/Tone';
import { formatDateTime } from '../../../lib/format';
import { useSourceOptions } from '../../../lib/app-settings';
import { useCanEdit } from '../../../stores/session';
import { useImport, useImports, useUploadImport } from '../api/imports-api';
import { ImportProgress } from './ImportProgress';
import { ImportSetup } from './ImportSetup';
import { IMPORT_STATUS_LABELS, IMPORT_STATUS_TONES } from './import-status';
import styles from './imports.module.css';

// R4 — stage ② Import on JOB-12 (§L33). With no import chosen: the drop zone (IMP-01's first
// half) and the job's past imports. With one chosen (`?import=`, which N-86 opens): its matching
// while it hasn't started (IMP-01), its progress and problems once it has (IMP-02).

const ACCEPT = '.csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

interface ImportStageProps {
  readonly detail: JobDetail;
  readonly importId: string | null;
  readonly onOpen: (importId: string | null) => void;
}

export function ImportStage({ detail, importId, onOpen }: ImportStageProps): ReactNode {
  const canImport = useCanEdit('application');
  if (!canImport) return <ForbiddenState resource="importing candidates" contact="Your admin" isInset />;
  return (
    <div className={styles.stage}>
      {importId ? <ChosenImport key={importId} importId={importId} onOpen={onOpen} /> : <StartImport detail={detail} onOpen={onOpen} />}
      <PastImports jobId={detail.id} currentId={importId} onOpen={onOpen} />
    </div>
  );
}

function ChosenImport({ importId, onOpen }: { readonly importId: string; readonly onOpen: (importId: string | null) => void }): ReactNode {
  const view = useImport(importId);
  if (view.isPending) return <SkeletonLines count={6} />;
  if (view.error) return <ServerErrorState onRetry={() => void view.refetch()} />;
  if (view.data.status === 'uploaded' || view.data.status === 'mapped') return <ImportSetup view={view.data} onOpen={onOpen} />;
  return <ImportProgress view={view.data} />;
}

/** IMP-01's first half: pick where the file came from, then drop it. */
function StartImport({ detail, onOpen }: { readonly detail: JobDetail; readonly onOpen: (importId: string | null) => void }): ReactNode {
  const sources = useSourceOptions('record');
  const [source, setSource] = useState(DEFAULT_IMPORT_SOURCE);
  const [error, setError] = useState<string | null>(null);
  const upload = useUploadImport(detail.id);
  const running = useImports(detail.id).data?.find((item) => item.status === 'importing');

  const onFile = (file: File): void => {
    setError(null);
    upload.mutate(
      { file, source },
      {
        onSuccess: (view) => onOpen(view.id),
        onError: (failure) => setError(failure.fields.file ?? failure.message),
      },
    );
  };

  return (
    <section className={styles.panel} aria-labelledby="import-start-title">
      <div className={styles.panelHead}>
        <div>
          <h2 id="import-start-title" className={styles.title}>
            Import applicants
          </h2>
          <p className={styles.lead}>Drop the export from your form, Google Forms or a job site. Each row becomes a candidate on this job, and their CVs are downloaded from the links.</p>
        </div>
      </div>
      {running && (
        <div className={styles.banner} data-tone="progress">
          <span>
            <strong>{running.fileName}</strong> is importing now.
          </span>
          <Button variant="ghost" size="sm" onClick={() => onOpen(running.id)}>
            See progress
          </Button>
        </div>
      )}
      <div className={styles.settingsRow}>
        <div className={styles.sourceField}>
          <Field label="Where the file came from" hint="Every candidate imported is recorded as coming from here.">
            {({ inputId, describedBy }) => <Select id={inputId} describedBy={describedBy} label="Where the file came from" options={sources} value={source} onChange={setSource} />}
          </Field>
        </div>
      </div>
      <DropZone
        label="Choose the applicants' file"
        title="Drop the applicants' CSV here"
        hint="CSV (UTF-8) or Excel · up to 20 MB and 5,000 rows"
        accept={ACCEPT}
        isPending={upload.isPending}
        error={error}
        onFile={onFile}
      />
    </section>
  );
}

/** IMP-02 "Past imports listed below". */
function PastImports({ jobId, currentId, onOpen }: { readonly jobId: string; readonly currentId: string | null; readonly onOpen: (importId: string | null) => void }): ReactNode {
  const list = useImports(jobId);
  const items = (list.data ?? []).filter((item) => item.id !== currentId && item.status !== 'discarded');
  if (items.length === 0 && !currentId) return null;
  return (
    <section className={styles.panel} aria-labelledby="import-past-title">
      <div className={styles.panelHead}>
        <h2 id="import-past-title" className={styles.subtitle}>
          Past imports
        </h2>
        {currentId && (
          <Button variant="outline" size="sm" icon={Upload} onClick={() => onOpen(null)}>
            Import another file
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <p className={styles.muted}>No other imports on this job yet.</p>
      ) : (
        <ul className={styles.pastList}>
          {items.map((item) => (
            <PastImport key={item.id} item={item} onOpen={onOpen} />
          ))}
        </ul>
      )}
    </section>
  );
}

function PastImport({ item, onOpen }: { readonly item: ImportListItem; readonly onOpen: (importId: string | null) => void }): ReactNode {
  return (
    <li className={styles.pastItem}>
      <button type="button" className={styles.pastButton} onClick={() => onOpen(item.id)}>
        <span className={styles.pastName}>{item.fileName}</span>
        <ToneChip tone={IMPORT_STATUS_TONES[item.status]}>{IMPORT_STATUS_LABELS[item.status]}</ToneChip>
      </button>
      <span className={styles.pastMeta}>
        {applicationSourceLabel(item.source)} · {item.summary} · {formatDateTime(item.createdAt)}
        {item.createdByName ? ` · ${item.createdByName}` : ''}
      </span>
    </li>
  );
}
